"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTabA11y = void 0;
const kebabCase_1 = __importDefault(require("lodash/kebabCase"));
/**
 * A React hook that prepares accessibility (a11y) props for the `Tabs` and `DropdownTabs` components
 * and corresponding tab panel element.
 *
 * The `tabsA11yProps` should be spread to the `Tabs` or `DropdownTabs` component.
 *
 * The `tabPanelA11yProps` should be called with the ID of the tab associated with
 * the tab panel and the result spread to the element serving as the tab panel.
 *
 * @example
 * ```typescript
 *   const tabs = ['tab1', 'tab2', 'tab3'];
 *   const [activeTab, setActiveTab] = useState<string|undefined>(undefined);
 *   const { tabsA11yProps, tabPanelA11yProps } = useTabA11y({
 *     labelText: 'Tab example'
 *   });
 *   return (
 *     <>
 *       <Tabs
 *         tabs={tabs}
 *         activeTab={activeTab}
 *         onActivate={(_, tabId) => setActiveTab(tabId)}
 *         {...tabsA11yProps}
 *       />
 *       {activeTab === 'tab1' && (
 *         <div {...tabPanelA11yProps('tab1')}>
 *           This is content for Tab 1
 *         </div>
 *       )}
 *       {activeTab === 'tab2' && (
 *         <div {...tabPanelA11yProps('tab2')}>
 *           This is content for Tab 2
 *         </div>
 *       )}
 *       {activeTab === 'tab3' && (
 *         <div {...tabPanelA11yProps('tab3')}>
 *           This is content for Tab 3
 *         </div>
 *       )}
 *     </>
 *   );
 * ```
 *
 * @param props the input props for the hook
 * @returns accessibility (a11y) props for the `Tabs` component and corresponding tab panel element.
 */
const useTabA11y = ({ labelText, labelElementId, tabControlIdPrefix = 'tab', tabPanelIdPrefix = 'tab-panel', activeTabHasFocusableContent = false }) => {
    const getTabControlId = (tabId) => `${tabControlIdPrefix}-${(0, kebabCase_1.default)(tabId)}`;
    const getTabPanelId = (tabId) => `${tabPanelIdPrefix}-${(0, kebabCase_1.default)(tabId)}`;
    return {
        tabsA11yProps: Object.assign(Object.assign(Object.assign({}, (labelText ? { 'aria-label': labelText } : {})), (labelElementId ? { 'aria-labelledby': labelElementId } : {})), { getTabControlId,
            getTabPanelId }),
        tabPanelA11yProps: (tabId) => ({
            id: getTabPanelId(tabId),
            'aria-labelledby': getTabControlId(tabId),
            role: 'tabpanel',
            tabIndex: activeTabHasFocusableContent ? undefined : 0
        })
    };
};
exports.useTabA11y = useTabA11y;
//# sourceMappingURL=useTabA11y.js.map